import type { BatchPersistedTabContent } from 'apiConnector/tabs/tabs.model';
import type { ProductVariation } from 'models/productsReferential';
import type { AppState } from 'state/state.model';
import { getTabType } from 'state/workspaces/tabs/tabs.selector';

import { areSelectedFieldsInvalid, isNbOfBatchFormWithinThreshold, isXMarginFieldInvalid } from './tabContent.helpers';
import type { BatchWorkspaceTabContent, BatchWorkspaceTabContentState } from './tabContent.model';

function selectBatchWorkspaceTabContentState(state: AppState): BatchWorkspaceTabContentState {
  return state.workspaces.newBatch.tabContent;
}

export function getBatchTabContent(appState: AppState, tabId: string): BatchWorkspaceTabContent | undefined {
  const state = selectBatchWorkspaceTabContentState(appState);
  return state[tabId];
}

export function hasBatchTabContent(appState: AppState, tabId: string): boolean {
  return getBatchTabContent(appState, tabId) === undefined ? false : true;
}

export function isFetchingBatchTabContent(appState: AppState, tabId: string): boolean {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.isFetchingTabContent ?? false;
}

export function getBatchProductType(appState: AppState, tabId: string): string | undefined {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.productType : undefined;
}

export function isBatchProductValidated(appState: AppState, tabId: string): boolean {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.isProductValidated : false;
}

export function getBatchProductSubType(appState: AppState, tabId: string): string | undefined {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.productSubtype : undefined;
}

export function getBatchProductVariation(appState: AppState, tabId: string): Partial<ProductVariation> {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch'
    ? {
        productType: tabContent.productType!,
        productSubType: tabContent.productSubtype!,
        wrapper: tabContent.wrapper!,
      }
    : {};
}

export function getBatchWrapper(appState: AppState, tabId: string): string | undefined {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.wrapper : undefined;
}

export function getBatchPayoffType(appState: AppState, tabId: string): string | undefined {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.payoffType : undefined;
}

export function getFormIdsInBatchTab(appState: AppState, tabId: string): readonly string[] {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.forms.map(form => form.negotiationId) : [];
}

export function canAddNewFormInBatchTab(appState: AppState, tabId: string): boolean {
  const tabContent = getBatchTabContent(appState, tabId);
  if (!tabContent || tabContent.workspace !== 'newBatch') {
    return false;
  }
  const totalNumberOfForms = getNbOfFormsInBatchTab(appState, tabId) + 1;
  return isNbOfBatchFormWithinThreshold(totalNumberOfForms);
}

export function getNbOfFormsInBatchTab(appState: AppState, tabId: string): number {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch' ? tabContent.forms.length : 0;
}

export function getBatchTabIdByFormId(appState: AppState, formId: string): string | undefined {
  const state = selectBatchWorkspaceTabContentState(appState);
  const matchedTab = Object.values(state).find(
    tabContent => tabContent.workspace === 'newBatch' && tabContent.forms.some(form => form.negotiationId === formId),
  );
  return matchedTab?.tabId ?? undefined;
}

export function getBatchTabContentToPersist(appState: AppState, tabId: string): BatchPersistedTabContent | undefined {
  const tabContent = getBatchTabContent(appState, tabId);
  return tabContent?.workspace === 'newBatch'
    ? {
        workspace: 'newBatch',
        tabId,
        negotiationIds: tabContent.formIds,
        forms: tabContent.forms,
        productType: tabContent.productType,
        productSubtype: tabContent.productSubtype,
        wrapper: tabContent.wrapper,
      }
    : undefined;
}

export function getBatchTabIdToReloadForm(
  appState: AppState,
  productType?: string,
  productSubType?: string,
  wrapper?: string,
): string | undefined {
  if (!productType || !productSubType) {
    return undefined;
  }
  const state = selectBatchWorkspaceTabContentState(appState);
  return Object.values(state).find(tabContent => {
    const tabType = getTabType(appState, tabContent.tabId);
    return (
      tabType === 'reprice' &&
      tabContent.productType === productType &&
      tabContent.productSubtype === productSubType &&
      tabContent.wrapper === wrapper
    );
  })?.tabId;
}

export function canImportQuotesToBatchTab(appState: AppState, tabId: string): boolean {
  const nbOfForms = getNbOfFormsInBatchTab(appState, tabId);
  const minNbOfFormsAfterImport = nbOfForms + 1;
  return isNbOfBatchFormWithinThreshold(minNbOfFormsAfterImport);
}

export function isMultipleApplyOpened(appState: AppState, tabId: string): boolean {
  return getBatchTabContent(appState, tabId)?.isMultipleApplyOpened ?? false;
}
export function getMultipleBatchApplySelectedFieldsIds(appState: AppState, tabId: string): readonly string[] {
  return getBatchTabContent(appState, tabId)?.selectedFieldsIds ?? [];
}

export function getMultipleBatchApplyFieldsData(appState: AppState, tabId: string) {
  return getBatchTabContent(appState, tabId)?.fields ?? {};
}
export function getMultipleBatchApplyFieldsActions(appState: AppState, tabId: string) {
  return getBatchTabContent(appState, tabId)?.updateActions ?? {};
}

export function isMultipleApplyApplyButtonDisabled(appState: AppState, tabId: string) {
  const tabContent = getBatchTabContent(appState, tabId);
  return !tabContent ? true : areSelectedFieldsInvalid(tabContent) || isXMarginFieldInvalid(tabContent);
}

export function getIsDeleteFormConfirmationModalOpen(appState: AppState, tabId: string) {
  return getBatchTabContent(appState, tabId)?.isDeleteFormConfirmationModalOpen ?? false;
}
